import _ from 'lodash';
import moment, { Moment } from 'moment';
import { v4 } from 'uuid';
import { ChartFragment } from 'models/api-response';
import { DosageTime, MarDosageRecord } from 'models/ui';
import {
  DATE_FORMAT,
  DATE_FORMAT_MAR,
  DateFormatByLocale,
  DateTimeFormatByLocale,
  ISO_8601_FORMAT,
  ISO_FORMAT,
  Locales,
  MOMENT_UNIT_DAYS,
  MOMENT_UNIT_MINUTES,
  TIME_FORMAT
} from 'constants/app.constant';
import { chartHelper } from 'helpers';
import { toMomentWithFormat } from 'helpers/datetime.helper';
import { FormField as PharmacyFormField } from 'components/features/chart/order-entry/pharmacy/constants';
import { AUTHOR_MAR_DAYS, FormField, INACTIVE_TYPE, ORDER_TYPES, SPECIAL_DOSAGE_MAPPING_RULES } from './constants';
import { MarAdministerFormField } from './MarAdministerSidebar';
import { MarFilterDayItem } from './MarView';
import dosageTimesMappingMap, { DosageTimeMap } from './dosageTimesMapping';

type HandleFuncInput = {
  startDate: Moment;
  endDateTime: Moment;
  dosageTimes: MarDosageRecord['dosageTimes'];
  isInValidRange: Function;
  dateTimeBetween: string[];
  locale: Locales;
  repeatTimes?: number;
  minutesApart?: number;
  isTopOfTheHour?: boolean;
  frequency?: string;
  minutesAfterStartTime?: number;
  isIgnored?: Function;
  getDosageTimeByFrequency?: Function;
  isStop?: Function;
  getDosageTimeByAfterStartTime?: Function;
  getDosageTimeObject?: Function;
  getAdministeredDosageTimes?: Function;
  getDosageTimeByAfterFirstDose?: Function;
  getDosageTimeByTimesInAWeek?: Function;
};

export const handleAfterStartTime = ({
  startDate,
  isTopOfTheHour,
  frequency,
  isInValidRange,
  endDateTime,
  dosageTimes,
  minutesAfterStartTime,
  repeatTimes,
  dateTimeBetween,
  locale,
  isStop,
  getDosageTimeByAfterStartTime
}: HandleFuncInput): void => {
  for (let indexDateTime = startDate.clone(), index = 0; indexDateTime <= endDateTime; indexDateTime.add(1, MOMENT_UNIT_DAYS), index += 1) {
    if (isStop(repeatTimes, index)) {
      break;
    }
    const dosageDate = indexDateTime.format(DateFormatByLocale[locale]);
    const dosageTime = isTopOfTheHour ? '00:00' : indexDateTime.format(TIME_FORMAT);
    const firstOffset = toMomentWithFormat(`${dosageDate} ${dosageTime}`, Object.values(DateTimeFormatByLocale))
      .add(minutesAfterStartTime, MOMENT_UNIT_MINUTES)
      .format(DateTimeFormatByLocale[locale]);
    const dosageTimeObject = getDosageTimeByAfterStartTime(dosageTimes, firstOffset, frequency, isInValidRange);
    dateTimeBetween.push(...dosageTimeObject);
  }
};

export const handleAfterFirstDose = ({
  dosageTimes,
  dateTimeBetween,
  startDate,
  repeatTimes,
  locale,
  isStop,
  isInValidRange,
  endDateTime,
  minutesApart,
  getDosageTimeObject,
  getAdministeredDosageTimes,
  getDosageTimeByAfterFirstDose
}: HandleFuncInput): void => {
  const allAdministeredDosages = getAdministeredDosageTimes(dosageTimes) ?? [];
  for (let indexDateTime = startDate.clone(); indexDateTime <= endDateTime; indexDateTime.add(1, MOMENT_UNIT_DAYS)) {
    dateTimeBetween.push(getDosageTimeObject(indexDateTime.format(DateFormatByLocale[locale]), 'Administer'));
  }
  if (!dosageTimes || dosageTimes.length === 0) {
    return;
  }

  const firstDosage = allAdministeredDosages[0];
  allAdministeredDosages.forEach((t) => dateTimeBetween.push(t));
  let offset = 0;

  for (
    let indexDateTime = toMomentWithFormat(firstDosage.administeredDate, Object.values(DateTimeFormatByLocale)).clone(), index = 0;
    indexDateTime <= endDateTime;
    indexDateTime.add(1, MOMENT_UNIT_DAYS), index += 1
  ) {
    if (isStop(repeatTimes, index)) {
      break;
    }
    const timeEntries = [];
    const dosageDate = indexDateTime.format(DateFormatByLocale[locale]);

    const latestTimeOfDay = toMomentWithFormat(`${dosageDate} 00:00`, Object.values(DateTimeFormatByLocale)).add(1, MOMENT_UNIT_DAYS);
    let dosageItem;

    do {
      dosageItem = toMomentWithFormat(`${firstDosage.administeredDate} ${firstDosage.administeredTime}`, Object.values(DateTimeFormatByLocale))
        .add(offset * minutesApart, MOMENT_UNIT_MINUTES)
        .format(DateTimeFormatByLocale[locale]);
      offset += 1;
      if (toMomentWithFormat(dosageItem, Object.values(DateTimeFormatByLocale)).isSameOrBefore(latestTimeOfDay)) {
        timeEntries.push(dosageItem);
      } else {
        offset -= 1;
      }
    } while (toMomentWithFormat(dosageItem, Object.values(DateTimeFormatByLocale)).isSameOrBefore(latestTimeOfDay) && offset <= repeatTimes);
    const dosageTimeObject = getDosageTimeByAfterFirstDose(timeEntries, dosageTimes, isInValidRange);
    dateTimeBetween.push(...dosageTimeObject);
  }
};

export const handleTimesInAWeek = ({
  startDate,
  endDateTime,
  dosageTimes,
  frequency,
  isInValidRange,
  dateTimeBetween,
  locale,
  getDosageTimeByTimesInAWeek
}: HandleFuncInput): void => {
  for (let indexDateTime = startDate.clone(); indexDateTime <= endDateTime; indexDateTime.add(1, MOMENT_UNIT_DAYS)) {
    const dosageDate = indexDateTime.format(DateFormatByLocale[locale]);
    const dosageTime = indexDateTime.format(TIME_FORMAT);
    const dosageTimeObject = getDosageTimeByTimesInAWeek(dosageTimes, frequency, dosageDate, dosageTime, isInValidRange);
    dateTimeBetween.push(...dosageTimeObject);
  }
};

export const handleNormal = ({ startDate, endDateTime, dosageTimes, frequency, isInValidRange, dateTimeBetween, locale, getDosageTimeByFrequency }: HandleFuncInput): void => {
  for (let indexDateTime = startDate.clone(); indexDateTime <= endDateTime; indexDateTime.add(1, MOMENT_UNIT_DAYS)) {
    const dosageDate = indexDateTime.format(DateFormatByLocale[locale]);
    const dosageTime = indexDateTime.format(TIME_FORMAT);
    const dosageTimeObject = getDosageTimeByFrequency(dosageTimes, frequency, dosageDate, dosageTime, isInValidRange);
    dateTimeBetween.push(...dosageTimeObject);
  }
};

export const handleOnceThenDropOff = ({
  startDate,
  dosageTimes,
  dateTimeBetween,
  endDateTime,
  frequency,
  isInValidRange,
  locale,
  getAdministeredDosageTimes,
  isIgnored,
  getDosageTimeByFrequency
}: HandleFuncInput): void => {
  const administerDosageTimes = getAdministeredDosageTimes(dosageTimes);
  // if we have an administered dose then we are done.
  if (administerDosageTimes.length > 0) {
    dateTimeBetween.push(...administerDosageTimes);
  } else {
    for (let indexDateTime = startDate.clone(), index = 1; indexDateTime <= endDateTime; indexDateTime.add(1, MOMENT_UNIT_DAYS), index += 1) {
      const dosageDate = indexDateTime.format(DateFormatByLocale[locale]);
      const dosageTime = indexDateTime.format(TIME_FORMAT);
      if (administerDosageTimes.length === 0 || isIgnored(administerDosageTimes, dosageDate) !== undefined) {
        const dosageTimeObject = getDosageTimeByFrequency(dosageTimes, frequency, dosageDate, dosageTime, isInValidRange);
        dateTimeBetween.push(...dosageTimeObject);
      }
    }
  }
};

// Resolves the charting time that anchors the MAR window. Author mode has no saved charting time, so
// it falls back to now shifted back AUTHOR_MAR_DAYS-1 days, giving a "today + previous 6 days" window
// (SIMSNG-5841). Non-author flows fall back to now unchanged. An explicit charting time is returned as-is.
export const resolveMarChartingTime = (chartingTime: string, isAuthor: boolean): string => {
  if (chartingTime) {
    return chartingTime;
  }
  const base = moment().utc();
  if (isAuthor) {
    base.subtract(AUTHOR_MAR_DAYS - 1, MOMENT_UNIT_DAYS);
  }
  return base.format(ISO_8601_FORMAT);
};

export const getPharmacyDataAndAdministeredDosageTimes = (fragments: ChartFragment[], locale: Locales, isAuthor: boolean, chartingTime?: string) => {
  const { getOrderDescription } = chartHelper;
  const administeredDosageTimes = [];
  const pharmacyData = fragments.map((item) => {
    const { chartData } = item;
    const dose = chartData.specialDose ? `${chartData.specialDose} ${chartData.doseUnits}` : chartData.medicationDoseTextbox || chartData.medicationDose;
    let { orderStartDate, orderStartTime, orderStopDate, orderStopTime } = chartData;
    const { dosageTimes = [] } = chartData;
    if (isAuthor) {
      // create a dummy start/stop date/time for author flow, spanning a multi-day window so dosages
      // can be authored across multiple days (SIMSNG-5841) rather than being limited to a single day.
      // The redesigned MAR builds day rows forward from the (already back-shifted) charting anchor, so
      // the window here is [anchor, anchor + (AUTHOR_MAR_DAYS - 1)]. Only scheduled/routine orders span
      // the full window; Stat (one-time) and PRN (as-needed) orders stay single-day and land on the
      // window end (the current day) rather than being expanded across the window. These dates are
      // placeholders - the real dates are resolved from the phase start time + offset at case-study start.
      const isScheduled = chartData.orderType === 'Routine' || chartData.orderType === 'Custom';
      const anchor = chartingTime ? moment(chartingTime, [moment.ISO_8601, ISO_FORMAT]) : moment();
      const windowEnd = anchor.clone().add(AUTHOR_MAR_DAYS - 1, MOMENT_UNIT_DAYS);
      orderStartDate = (isScheduled ? anchor : windowEnd).format(DateFormatByLocale[locale]);
      orderStartTime = '00:00';
      orderStopDate = windowEnd.format(DateFormatByLocale[locale]);
      orderStopTime = '23:59';
    }
    administeredDosageTimes.push(...dosageTimes);
    return {
      fragmentId: item.fragmentId,
      drugName: getOrderDescription(item),
      orderType: chartData.orderType,
      orderStartDate,
      orderStartTime,
      orderStopDate,
      orderStopTime,
      [PharmacyFormField.HAS_STOP_DATE_TIME_DEFAULT]: chartData[PharmacyFormField.HAS_STOP_DATE_TIME_DEFAULT],
      holdDate: chartData[FormField.HOLD_DATE] || orderStartDate,
      holdTime: chartData[FormField.HOLD_TIME] || '00:00',
      holdComments: chartData.holdComments,
      holdFirstInitial: chartData.holdFirstInitial,
      holdLastInitial: chartData.holdLastInitial,
      isHold: chartData.isHold,
      discontinueDate: chartData[FormField.DISCONTINUE_DATE] || orderStartDate,
      discontinueTime: chartData[FormField.DISCONTINUE_TIME] || '00:00',
      discontinueComments: chartData.discontinueComments,
      discontinueFirstInitial: chartData.discontinueFirstInitial,
      discontinueLastInitial: chartData.discontinueLastInitial,
      isDiscontinued: chartData.isDiscontinued,
      orderStart: `${orderStartDate} ${orderStartTime}`.trim(),
      orderStop: `${orderStopDate} ${orderStopTime}`.trim(),
      dose,
      route: chartData.routeForAdministration,
      frequency: chartData.frequencyOfAdministration,
      specialInstructions: chartData.specialInstructions,
      dosageTimes: chartData.dosageTimes === undefined ? [] : chartData.dosageTimes
    };
  });

  return { pharmacyData, administeredDosageTimes };
};

export const isChartedDosageTime = (dosageTime): boolean => Boolean(dosageTime?.isAdministered || dosageTime?.isHeld);

export const ROUTE_IV_INFUSION = 'IV Infusion';

export const isScheduledOrder = (record): boolean => (record?.orderType === 'Routine' || record?.orderType === 'Custom') && record?.route !== ROUTE_IV_INFUSION;

export const getHoldableDosageTimes = (record): MarDosageRecord['dosageTimes'] =>
  (record?.dosageTimes ?? []).filter((dosageTime) => !dosageTime.isAdministered && !dosageTime.isHeld && dosageTime.dosageTime !== 'Administer');

export const applyDosageHold = (record, targetDosages: DosageTime[], holdFields: object): MarDosageRecord['dosageTimes'] => {
  const chartedDosageTimes = (record?.originDosageTimes ?? []).filter(isChartedDosageTime);

  return [...chartedDosageTimes, ...targetDosages.map((dosageTime) => ({ ...dosageTime, ...holdFields, [FormField.IS_HELD]: true }))];
};

export const buildAdHocHeldDosage = (row): DosageTime => ({
  id: v4(),
  dosageDate: row.dosageTimeTable,
  dosageTime: row.orderStartTime || '00:00',
  administeredDate: row.dosageTimeTable
});

export const clearDosageHold = (dosageTimes, dosageIds: string[]): MarDosageRecord['dosageTimes'] => (dosageTimes ?? []).filter((dosageTime) => !dosageIds.includes(dosageTime.id));

export const buildUnholdFragment = (fragment: ChartFragment, dosageIds: string[], presaveFragment: Function): ChartFragment => ({
  ...presaveFragment(fragment),
  chartData: { ...fragment.chartData, dosageTimes: clearDosageHold(fragment.chartData.dosageTimes, dosageIds) },
  linkedFragmentId: fragment.linkedFragmentId || fragment.fragmentId
});

export const reorganizePharmacyDataToRecords = (pharmacyData, getDosageTime: Function, getInactiveOrdersType: Function) => {
  const statRecords = [];
  const continuousIVRecords = [];
  const scheduledRecords = [];
  const prnRecords = [];
  const inactiveOrderRecords = [];
  const firstMed = pharmacyData?.[0];
  let viewDateStart = toMomentWithFormat(firstMed?.orderStartDate, Object.values(DateTimeFormatByLocale));
  let viewDateStop = toMomentWithFormat(firstMed?.orderStopDate, Object.values(DateTimeFormatByLocale));
  pharmacyData.forEach((item) => {
    if (toMomentWithFormat(item.orderStartDate, Object.values(DateTimeFormatByLocale)).isBefore(viewDateStart)) {
      viewDateStart = toMomentWithFormat(item.orderStartDate, Object.values(DateTimeFormatByLocale));
    }
    if (toMomentWithFormat(item.orderStopDate, Object.values(DateTimeFormatByLocale)).isAfter(viewDateStop)) {
      viewDateStop = toMomentWithFormat(item.orderStopDate, Object.values(DateTimeFormatByLocale));
    }
    const defaultDosageTime = getDosageTime(item.dosageTimes, item.frequency, item.orderStartDate, item.orderStopDate, item.orderType, item.orderStartTime);
    const inActiveResult = getInactiveOrdersType(item);
    const newItem = { ...item, ...inActiveResult, dosageTimes: defaultDosageTime };
    if (newItem.inactiveType || newItem.status) {
      inactiveOrderRecords.push(newItem);
    } else if (newItem.route === ROUTE_IV_INFUSION) {
      continuousIVRecords.push(newItem);
    } else if (newItem.orderType === ORDER_TYPES.STAT) {
      statRecords.push(newItem);
    } else if (newItem.orderType === 'Routine' || newItem.orderType === 'Custom') {
      scheduledRecords.push(newItem);
    } else if (newItem.orderType === ORDER_TYPES.PRN) {
      prnRecords.push(newItem);
    }
  });

  return {
    statRecords,
    continuousIVRecords,
    scheduledRecords,
    prnRecords,
    inactiveOrderRecords,
    viewDateStart,
    viewDateStop
  };
};

export const getIsShowAdmin = (
  dosageTimesMapping: DosageTimeMap,
  frequency: string,
  dosageTimes,
  hideAdministration: boolean,
  isInactiveOrder: boolean,
  isAnyAdministered: boolean,
  orderType?: string
): boolean => {
  let showAdmin = false;
  const mapping = dosageTimesMapping.get(frequency);
  if (mapping) {
    // mapping, but not times (usually continuous IV)
    if (dosageTimes.length === 0) {
      showAdmin = true;
    } else {
      // if there any dosageTimes that are administer, show it
      dosageTimes.forEach((dt) => {
        if (dt.dosageTime === 'Administer') {
          showAdmin = true;
        }
      });
    }
    // if we dont have a mapping, show it
  } else {
    showAdmin = true;
  }
  // if the props say show it , show it
  showAdmin = showAdmin || !hideAdministration;
  // hide it if it is an inactive order
  showAdmin = showAdmin && !isInactiveOrder;
  if (isAnyAdministered && mapping?.type === SPECIAL_DOSAGE_MAPPING_RULES.AFTER_FIRST_DOSE && !mapping?.administrationTimes) showAdmin = false;
  // PRN ("as needed") orders can always be administered again for unlimited dosages,
  // regardless of the frequency mapping - only an inactive order stops that (SIMSNG-5967).
  if (orderType === ORDER_TYPES.PRN) showAdmin = !isInactiveOrder;
  return showAdmin;
};

export const retainAdministeredDosageTimes = (generated, dosageTimes) => {
  const administered = (dosageTimes ?? []).filter((dosageTime) => dosageTime.isAdministered);
  const missing = administered.filter((dose) => !generated.some((item) => item.id === dose.id));
  return [...generated, ...missing];
};

/**
 * Expands each order into one row per day, within the 7-day charting window
 * [chartingTime, chartingTime + 6 days] intersected with the order's active
 * range [orderStartDate, orderStopDate].
 *
 * For each day in the overlapping range, only days whose weekday (dddd) is
 * included in selectedMarDay are emitted. Returns an empty array when
 * chartingTime is missing or the order does not overlap the charting window.
 *
 * @param orders - The list of orders to expand by day.
 * @param chartingTime - Start of the charting window (DATE_FORMAT). When omitted, returns [].
 * @param administerListFlag - When true, filters dosageTimes to those matching the row's date (inactive orders keep all dosageTimes); when false, dosageTimes are passed through unchanged.
 * @param selectedMarDay - Allowed weekdays for emitted rows, e.g. ['Sunday', 'Monday', ...].
 * @param isSTAT - When true, only expands the order for the charting day (0 days); when false, expands for the full 7-day window.
 * @returns A flattened array of expanded orders, one entry per day, each carrying its dosageTimeTable and the corresponding dosageTimes.
 */
export const expandOrdersByDate = (orders, chartingTime?: string, administerListFlag = false, selectedMarDay: string[] = [], isSTAT = false) => {
  if (!chartingTime) return [];

  const windowStart = moment(chartingTime, [moment.ISO_8601, ISO_FORMAT]).startOf('day');
  const windowEnd = windowStart.clone().add(6, 'days');

  return orders.flatMap((order) => {
    const startDate = moment(order.orderStartDate, DATE_FORMAT).startOf('day');
    const stopDate = moment(order.orderStopDate, DATE_FORMAT).startOf('day');

    const isOverlap = isSTAT ? startDate.isBetween(windowStart, windowEnd, 'day', '[]') : startDate.isSameOrBefore(windowEnd, 'day') && stopDate.isSameOrAfter(windowStart, 'day');
    if (!isOverlap) return [];

    const fromDate = moment.max(windowStart, startDate);
    const effectiveStopDate = moment.min(stopDate, windowEnd);
    const daysToGenerate = _.clamp(effectiveStopDate.diff(fromDate, 'days') + 1, 0, isSTAT ? 1 : 7);

    const dosageTimesFor = (currentDate) => {
      if (!administerListFlag || order.isInactiveOrder) return order.dosageTimes;
      return order.dosageTimes.filter((dt) => dt.dosageDate === currentDate.format(DATE_FORMAT));
    };

    return _.range(daysToGenerate)
      .map((i) => fromDate.clone().add(i, 'days'))
      .filter((currentDate) => selectedMarDay.includes(currentDate.format('dddd')))
      .map((currentDate) => ({
        ...order,
        dosageTimes: dosageTimesFor(currentDate),
        dosageTimeTable: currentDate.format(DATE_FORMAT)
      }));
  });
};

const mappingDosageTimeByViewDateEHR = (item) => {
  return { ...item, originDosageTimes: item.dosageTimes };
};

const mappingIsAnyAdministered = (item) => {
  const isAnyAdministered = item.dosageTimes?.some((dosageTime) => dosageTime.isAdministered);
  return { ...item, originDosageTimes: item.dosageTimes, isAnyAdministered };
};

export const filterOrdersByChartingRange = (chartingTime, order) => {
  if (!chartingTime) return false;
  const chartingStart = moment(chartingTime, [moment.ISO_8601, ISO_FORMAT]).startOf('day');
  const chartingEnd = chartingStart.clone().add(6, 'days').endOf('day');

  const orderStart = moment(order.orderStartDate, DATE_FORMAT).startOf('day');
  const orderStop = moment(order.orderStopDate, DATE_FORMAT).endOf('day');

  return orderStart.isSameOrBefore(chartingEnd) && orderStop.isSameOrAfter(chartingStart);
};

export const sortByDosageTime = (dosageTimeList) => {
  const toMinutes = (t) => {
    const m = moment(t, 'HH:mm', true);
    if (!m.isValid()) return Infinity;
    return m.hours() * 60 + m.minutes();
  };
  return [...dosageTimeList].sort((a, b) => toMinutes(a.dosageTime || '00:00') - toMinutes(b.dosageTime || '00:00'));
};

const remapAdministerRecords = (records, hideAdministration, selectedMarDay: MarFilterDayItem[], chartingTime?: string, isSTAT = false) => {
  const filteredData = records
    ?.map((item) => mappingIsAnyAdministered(item))
    .map((item) => mappingDosageTimeByViewDateEHR(item))
    .filter((item) => filterOrdersByChartingRange(chartingTime, item));

  const separateRecords = expandOrdersByDate(filteredData, chartingTime, true, selectedMarDay, isSTAT);
  const separateTime = [];
  separateRecords.map((record) => {
    if (record.dosageTimes.length === 0) {
      return separateTime.push({ ...record, dosageTimeSeparate: record?.orderStartTime });
    }
    const { inactiveType, isHold, isDiscontinued, orderType, originDosageTimes, dosageTimeTable, frequency, dosageTimes, isInactiveOrder, isAnyAdministered } = record;
    const isAdminDisabled = isHold || inactiveType;
    const isItemDisabled = isDiscontinued || inactiveType === INACTIVE_TYPE.EXPIRED;
    const isAllDosageAdministered =
      (orderType === ORDER_TYPES.IV || orderType === 'Custom') &&
      originDosageTimes?.filter((dosageTime) => dosageTime.dosageDate === dosageTimeTable).every((dosageTime) => dosageTime.isAdministered);
    const showAdmin = getIsShowAdmin(dosageTimesMappingMap, frequency, dosageTimes, hideAdministration, isInactiveOrder, isAnyAdministered, orderType);
    const isAdministerDisabled = isAdminDisabled || isItemDisabled || (isAllDosageAdministered && !showAdmin);
    if (isAdministerDisabled) return record;
    if (!isAdministerDisabled && showAdmin) {
      return separateTime.push({ ...record, dosageTimeSeparate: record?.orderStartTime });
    }
    return sortByDosageTime(record.originDosageTimes)
      .filter((item) => item.dosageDate === record.dosageTimeTable && !item.isAdministered && !item.isHeld)
      .map((item) => separateTime.push({ ...record, dosageTimeSeparate: item.dosageTime !== 'Administer' ? item.dosageTime : record?.orderStartTime }));
  });
  return separateTime;
};

export const getAllAdministerRecords = (allRecords, selectedMarDay: MarFilterDayItem[], chartingTime?: string) => {
  const { statRecords, scheduledRecords, prnRecords, continuousIVRecords } = allRecords;
  const statRecordsMapped = remapAdministerRecords(statRecords, false, selectedMarDay, chartingTime, true);
  const scheduledRecordsMapped = remapAdministerRecords(scheduledRecords, true, selectedMarDay, chartingTime);
  const prnRecordsMapped = remapAdministerRecords(prnRecords, false, selectedMarDay, chartingTime);
  const continuousIVRecordsMapped = remapAdministerRecords(continuousIVRecords, false, selectedMarDay, chartingTime);
  return [...statRecordsMapped, ...scheduledRecordsMapped, ...prnRecordsMapped, ...continuousIVRecordsMapped];
};

export const renderDosageDateTime = (formFieldItem: MarAdministerFormField, showDay = true) => {
  const momentDate = moment(formFieldItem.dosageTimeTable);
  const date = momentDate.format(DATE_FORMAT_MAR);
  const day = momentDate.format('dddd');
  const times =
    (formFieldItem.dosageTimes || [])
      .filter((item) => item.dosageDate === formFieldItem.dosageTimeTable)
      .map((item) => item.dosageTime)
      .join(', ') || formFieldItem.orderStartTime;
  return [date, times, showDay ? day : null].filter(Boolean).join(' | ');
};

export const findClosestDosageTime = (items, timeInput) => {
  const inputTime = moment(timeInput, 'HH:mm');

  const result = (items || []).reduce(
    (acc, item) => {
      const dosageTime = moment(item.dosageTime, 'HH:mm');
      const diff = Math.abs(inputTime.diff(dosageTime, 'minutes'));
      const circularDiff = Math.min(diff, 1440 - diff);

      if (circularDiff < acc.minDiff) {
        return {
          minDiff: circularDiff,
          closest: item.dosageTime
        };
      }

      return acc;
    },
    { minDiff: Infinity, closest: null }
  );

  return result.closest ?? '00:00';
};

/**
 * Converts a date string from "MM-DD-YYYY" format to "DD MMM YYYY" format.
 * The rest of the string remains unchanged.
 *
 * @param {string} dateString - The input string containing a date in "MM-DD-YYYY" format.
 * @returns {string} The string with the date converted to "DD MMM YYYY" format.
 *
 * @example
 * convertDateFormat("07-14-2026 00:00 - 12 Discontinued");
 *  Returns: "14 Jul 2026 00:00 - 12 Discontinued"
 *
 * @example
 * convertDateFormat("01-01-2026 12:30 - 5 Active");
 *  Returns: "01 Jan 2026 12:30 - 5 Active"
 */
export const convertDateFormat = (dateString) => {
  return dateString.replace(/(\d{2}-\d{2}-\d{4})/, (match) => moment(match, DATE_FORMAT).format(DATE_FORMAT_MAR));
};

export const getEarliestOrderStartDate = (allRecords) => {
  const all = [...allRecords.continuousIVRecords, ...allRecords.prnRecords, ...allRecords.scheduledRecords, ...allRecords.statRecords];

  const dates = all.filter((r) => r.orderStartDate).map((r) => moment.utc(r.orderStartDate, DATE_FORMAT));

  if (dates.length === 0) return null;

  return moment.min(dates).format(ISO_8601_FORMAT);
};

export const buildAdministerFragments = (renewArray, administerList, fragments, presaveFragment: Function, isAuthor?: boolean) => {
  const paramsMedication = [];
  renewArray.forEach((field) => {
    const matchedRecordFragment = administerList.find(
      (record) => record.fragmentId === field.id && record.dosageTimeTable === field.dosageTimeTable && record.dosageTimeSeparate === field.dosageTimeSeparate
    );
    if (!matchedRecordFragment) return;
    const { orderType, dosageTimeArray } = field;
    const selectedFragment = fragments.find((fragment) => fragment.fragmentId === matchedRecordFragment.fragmentId);
    if (!selectedFragment) return;
    const existedOriginDosageTimes = selectedFragment.chartData?.dosageTimes?.filter(isChartedDosageTime) || [];
    const dosageTimeMapped = [];
    dosageTimeArray.forEach((item) => {
      const dosageTime =
        field.dosageTimes?.find((dosage) => dosage.dosageDate === item.dosageTimeTable && dosage.dosageTime === item.dosageTimeSeparate && !dosage.isAdministered) || {};
      dosageTime[FormField.ADMINISTERED_TIME] = isAuthor ? undefined : item.administrationTime.value;
      dosageTime[FormField.ADMINISTERED_TIME_OFFSET] = isAuthor ? item.administrationTime.value : undefined;
      dosageTime[FormField.COMMENTS] = item.comment.value;
      dosageTime[FormField.FIRST_INITIAL] = item.initials.value.toUpperCase();

      dosageTime.isAdministered = true;

      if (orderType === ORDER_TYPES.PRN || orderType === ORDER_TYPES.STAT || orderType === ORDER_TYPES.IV) {
        dosageTime[FormField.ID] = v4();
        dosageTime[FormField.ADMINISTERED_DATE] = item.dosageTimeTable;
        dosageTime[FormField.DOSAGE_DATE] = item.dosageTimeTable;
        dosageTime[FormField.DOSAGE_TIME] = item.dosageTimeSeparate;
      }
      dosageTimeMapped.push(dosageTime);
    });
    const updatedDosageTimes = [...existedOriginDosageTimes, ...dosageTimeMapped];
    paramsMedication.push({
      ...presaveFragment(selectedFragment),
      chartData: { ...selectedFragment.chartData, dosageTimes: updatedDosageTimes },
      linkedFragmentId: selectedFragment.linkedFragmentId || selectedFragment.fragmentId
    });
  });
  return paramsMedication;
};
